import { parseBoardDocument } from "../persistence/board_schema";
import { formatStoredPath } from "../domain/paths";
import { parseTrashMetadata, TRASH_METADATA_FILE, type TrashMetadata } from "./trash_metadata";
import type { WorkspaceGateway } from "./workspace_gateway";

const textDecoder = new TextDecoder();

export interface ScannedBoard {
  readonly id: string | null;
  readonly kind: "board";
  readonly name: string;
  readonly path: readonly string[];
  readonly status: "valid" | "migration" | "invalid" | "newer";
  readonly updatedAt: string | null;
  readonly previewAvailable: boolean;
  readonly issue: string | null;
}

export interface ScannedFolder {
  readonly kind: "folder";
  readonly name: string;
  readonly path: readonly string[];
  readonly children: readonly WorkspaceNode[];
}

export type WorkspaceNode = ScannedBoard | ScannedFolder;

export interface WorkspaceScan {
  readonly children: readonly WorkspaceNode[];
  readonly boards: readonly ScannedBoard[];
  readonly folders: readonly ScannedFolder[];
}

export interface TrashedBoard extends ScannedBoard {
  readonly trashMetadata: TrashMetadata;
}

/** Recursively derives the visible folder tree and board recovery status from disk. */
export async function scanWorkspace(gateway: WorkspaceGateway): Promise<WorkspaceScan> {
  const boards: ScannedBoard[] = [];
  const folders: ScannedFolder[] = [];

  /** Traverses one organizational directory while treating board directories as leaves. */
  async function visit(path: readonly string[]): Promise<readonly WorkspaceNode[]> {
    const entries = await gateway.listDirectory(path);
    const children: WorkspaceNode[] = [];
    for (const entry of entries) {
      if (entry.kind !== "directory" || entry.name === ".shotboard") {
        continue;
      }
      const childPath = [...path, entry.name];
      const childEntries = await gateway.listDirectory(childPath);
      const hasBoardDocument = childEntries.some(
        (childEntry) => childEntry.kind === "file" && childEntry.name === "board.json",
      );
      if (hasBoardDocument) {
        const board = await scanBoard(gateway, childPath, childEntries);
        boards.push(board);
        children.push(board);
      } else {
        const folder: ScannedFolder = {
          kind: "folder",
          name: entry.name,
          path: childPath,
          children: await visit(childPath),
        };
        folders.push(folder);
        children.push(folder);
      }
    }
    return children.sort(compareNodes);
  }

  return { children: await visit([]), boards: boards.sort(compareBoards), folders };
}

/** Recursively derives the board list from directories containing board.json. */
export async function scanBoards(gateway: WorkspaceGateway): Promise<readonly ScannedBoard[]> {
  return (await scanWorkspace(gateway)).boards;
}

/** Scans completed trash entries while ignoring an interrupted partial destination. */
export async function scanTrash(gateway: WorkspaceGateway): Promise<readonly TrashedBoard[]> {
  const trashPath = [".shotboard", "trash"] as const;
  if (!(await gateway.fileExists(trashPath))) {
    return [];
  }
  const entries = await gateway.listDirectory(trashPath);
  const trashed: TrashedBoard[] = [];
  for (const entry of entries) {
    if (entry.kind !== "directory") {
      continue;
    }
    const path = [...trashPath, entry.name];
    const children = await gateway.listDirectory(path);
    if (
      !children.some((child) => child.kind === "file" && child.name === "board.json") ||
      !children.some((child) => child.kind === "file" && child.name === TRASH_METADATA_FILE)
    ) {
      continue;
    }
    try {
      const board = await scanBoard(gateway, path, children);
      const trashMetadata = parseTrashMetadata(
        textDecoder.decode(await gateway.readFile([...path, TRASH_METADATA_FILE])),
      );
      trashed.push({ ...board, trashMetadata });
    } catch {
      // Invalid trash metadata remains untouched for later recovery tooling.
    }
  }
  return trashed.sort(compareBoards);
}

/** Reads one board discriminator and preview presence without mutating it. */
async function scanBoard(
  gateway: WorkspaceGateway,
  path: readonly string[],
  entries: readonly { readonly kind: "file" | "directory"; readonly name: string }[],
): Promise<ScannedBoard> {
  const name = path.at(-1);
  if (name === undefined) {
    throw new Error("A board path must contain its directory name.");
  }
  const raw = textDecoder.decode(await gateway.readFile([...path, "board.json"]));
  const parsed = parseBoardDocument(raw);
  if (parsed.kind === "valid" || parsed.kind === "migration") {
    return {
      kind: "board",
      id: parsed.document.id,
      name,
      path,
      status: parsed.kind,
      updatedAt: parsed.document.updatedAt,
      previewAvailable: entries.some(
        (entry) => entry.kind === "file" && entry.name === "preview.webp",
      ),
      issue: parsed.kind === "migration" ? "Updates safely when opened." : null,
    };
  }
  return {
    kind: "board",
    id: null,
    name,
    path,
    status: parsed.kind,
    updatedAt: null,
    previewAvailable: false,
    issue: parsed.kind === "newer" ? `Requires schema ${parsed.schemaVersion}.` : parsed.reason,
  };
}

/** Sorts mixed navigator entries by visible name, then stable path. */
function compareNodes(left: WorkspaceNode, right: WorkspaceNode): number {
  return (
    compareNames(left.name, right.name) ||
    formatStoredPath(left.path).localeCompare(formatStoredPath(right.path))
  );
}

/** Sorts flattened boards with the same deterministic navigator collation. */
function compareBoards(left: ScannedBoard, right: ScannedBoard): number {
  return compareNodes(left, right);
}

/** Applies case-insensitive numeric name ordering without locale-specific persistence. */
function compareNames(left: string, right: string): number {
  return left.localeCompare(right, "en", { sensitivity: "base", numeric: true });
}
